import { AxiosError } from 'axios';
import { DefaultFilters, SavedFilters, StateMachineState, Test } from './types';
import { patientPropsSelector } from '../../../src/redux/selectors/patientSelector';
import { Subject } from '@lifeomic/phc-web-toolkit/src/common/types/Subject';
import { useMaskedMode } from '@lifeomic/phc-web-toolkit/src/hooks/useMaskedMode';
import * as React from 'react';
import GenomicsService from '@lifeomic/phc-web-toolkit/src/services/GenomicsService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { useActiveMrnConfig } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveMrnConfig';

const genomicsService = new GenomicsService();

export interface UseHydrateOmicsExplorer {
  defaultFilters?: DefaultFilters;
  savedFilters?: SavedFilters;
  state: StateMachineState;
  subject?: Subject;
  tests?: Array<Test>;
  isMaskedModeEnabled?: boolean;
  error?: AxiosError;
  refetchTests?: () => void;
}

/**
 * @description A hook for hydrating the Omics Explorer. It makes a call
 * to an endpoint to load all of the information to hydrate the UI given
 * a project and subject ID.
 */
export function useHydrateOmicsExplorer({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  activeProjectId,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  subjectId,
}): UseHydrateOmicsExplorer {
  const [hyrdateOmicsExplorer, hyrdateOmicsExplorerState] = useService(
    genomicsService.hydrateOmicsExplorer,
  );

  const [responseDefaultFilters, setResponseDefaultFilters] =
    React.useState<any>(null);
  const [responseSavedFilters, setResponseSavedFilters] =
    React.useState<any>(null);
  const [responseSubject, setResponseSubject] = React.useState<any>(null);
  const [responseTests, setResponseTests] = React.useState<any[]>([]);

  const [maybeMaskedAndProppedSubject, setMaybeMaskedAndProppedSubject] =
    React.useState<any>(null);

  const isMaskedModeEnabled = useMaskedMode();
  const mrnConfig = useActiveMrnConfig();

  React.useEffect(() => {
    if (!activeProjectId || !subjectId) {
      return;
    }

    hyrdateOmicsExplorer({ projectId: activeProjectId, subjectId });
  }, [activeProjectId, hyrdateOmicsExplorer, subjectId]);

  // Get the response from the API and set to local variables
  React.useEffect(() => {
    if (!hyrdateOmicsExplorerState?.response?.data) {
      return;
    }

    setResponseDefaultFilters(
      hyrdateOmicsExplorerState.response.data.defaultFilters,
    );
    setResponseSavedFilters(
      hyrdateOmicsExplorerState.response.data.savedFilters,
    );
    setResponseSubject(hyrdateOmicsExplorerState.response.data.subject);
    setResponseTests(hyrdateOmicsExplorerState.response.data.tests || []);
  }, [hyrdateOmicsExplorerState]);

  // We want to take the subject from the API response and run it
  // through our masked-mode checks and formatter
  React.useEffect(() => {
    if (!responseSubject) {
      return;
    }

    const maybeMaskedAndFormattedSubject = patientPropsSelector(
      responseSubject,
      isMaskedModeEnabled,
      mrnConfig,
    );

    setMaybeMaskedAndProppedSubject(maybeMaskedAndFormattedSubject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMaskedModeEnabled,
    responseSubject,
    mrnConfig.configType,
    mrnConfig?.code,
    mrnConfig.system,
  ]);

  const refetchTests = React.useCallback(() => {
    if (!activeProjectId || !subjectId) {
      return;
    }
    hyrdateOmicsExplorer({ projectId: activeProjectId, subjectId });
  }, [activeProjectId, subjectId, hyrdateOmicsExplorer]);

  // If we haven't gotten a subject back from the API yet,
  // we just return an initializing state
  if (!maybeMaskedAndProppedSubject && !hyrdateOmicsExplorerState.error) {
    return {
      state: 'INITIALIZING',
      refetchTests,
    };
  }

  // Otherwise it's safe to assume we are cleared to render and do
  // OE-things now
  return {
    defaultFilters: responseDefaultFilters || undefined,
    isMaskedModeEnabled,
    savedFilters: responseSavedFilters || undefined,
    state: hyrdateOmicsExplorerState.error ? 'ERROR' : 'COMPLETE',
    subject: maybeMaskedAndProppedSubject,
    tests: responseTests,
    error: hyrdateOmicsExplorerState.error,
    refetchTests,
  };
}
